import * as THREE from "three";
import { math, Signal } from "@engine";
import type { PhysicsWorld } from "@engine";
import type { Shape } from "../level/types";
import { shapeOf } from "../config/shapes";
import { GRAVITY_MAGNITUDE } from "../config/tunnel";

const ROTATE_DURATION = 0.14;

export class WorldRotator {
  readonly onRotated = new Signal<{ direction: 1 | -1; faceIndex: number }>();
  readonly onShapeApplied = new Signal<Shape>();

  private worldGroup: THREE.Object3D;
  private physics: PhysicsWorld;
  private shape: Shape = "octagon";
  private snapAngle: number = math.TAU / 8;
  private currentAngle = 0;
  private startAngle = 0;
  private targetAngle = 0;
  private tweenT = 0;
  private tweenDuration = ROTATE_DURATION;
  private faceIndex = 0;
  private locked = false;
  // ReverseGateFx flips this. We re-apply gravity each frame in
  // update(), so flipping the sign here is enough -- applyGravity()
  // reads gravitySignY when picking the gravity vector to push to
  // PhysicsWorld.
  private gravitySignY = -1;
  // Optional remap of "natural floor panel index" to "displayed
  // floor panel index". ReverseGateFx installs (i, sides) =>
  // (i + sides/2) % sides during the boss so TileHighlight + Score
  // both highlight the panel directly opposite the original floor
  // (which is now physically the new floor under flipped gravity).
  // null = identity (default).
  private floorOffsetFn: ((idx: number, sides: number) => number) | null = null;

  constructor(worldGroup: THREE.Object3D, physics: PhysicsWorld) {
    this.worldGroup = worldGroup;
    this.physics = physics;
    this.applyGravity();
  }

  setShape(shape: Shape, snapToNearestFace = true): void {
    if (this.shape === shape) return;
    this.shape = shape;
    this.snapAngle = shapeOf(shape).snapAngle;
    if (snapToNearestFace) {
      const nearestFace = Math.round(this.currentAngle / this.snapAngle);
      this.faceIndex = nearestFace;
      const targetAngle = nearestFace * this.snapAngle;
      this.startAngle = this.currentAngle;
      this.targetAngle = targetAngle;
      this.tweenT = 0;
      this.tweenDuration = ROTATE_DURATION;
    }
    this.onShapeApplied.emit(shape);
  }

  setLocked(locked: boolean): void {
    this.locked = locked;
  }

  rotate(direction: 1 | -1): void {
    if (this.locked) return;
    // Convention: with panel 0 at local -Y (floor), pressing LEFT
    // (direction=+1) should make the right-hand wall sweep DOWN to become
    // the new floor -- that's a CW rotation of the worldGroup (negative
    // angle around +Z when viewed from +Z), so we subtract here.
    this.faceIndex -= direction;
    this.startAngle = this.currentAngle;
    this.targetAngle = this.faceIndex * this.snapAngle;
    this.tweenT = 0;
    this.tweenDuration = ROTATE_DURATION;
    this.onRotated.emit({ direction, faceIndex: this.faceIndex });
  }

  reset(): void {
    this.currentAngle = 0;
    this.startAngle = 0;
    this.targetAngle = 0;
    this.faceIndex = 0;
    this.tweenT = 1;
    this.worldGroup.rotation.z = 0;
    // A fresh run never inherits a flipped gravity / floor offset
    // from a previous Reverse Gate boss.
    this.gravitySignY = -1;
    this.floorOffsetFn = null;
    this.applyGravity();
  }

  getCurrentAngle(): number {
    return this.currentAngle;
  }

  getFaceIndex(): number {
    return this.faceIndex;
  }

  getSnapAngle(): number {
    return this.snapAngle;
  }

  // Returns which local panelIndex of an N-sided segment is currently
  // sitting under the player, given the active world rotation. Used by
  // the score system to tell "ball landed on the new floor" (not a graze)
  // apart from "ball touched a side wall" (a real graze).
  //
  // ReverseGateFx remaps the result via setFloorOffset so the
  // "displayed" floor is the panel diametrically opposite during
  // the gravity-flip boss. The natural index stays correct for the
  // physics layer; only consumers of this getter see the remapped
  // panel.
  getFloorPanelIndex(sides: number): number {
    const natural = ((-this.faceIndex) % sides + sides) % sides;
    if (this.floorOffsetFn) return this.floorOffsetFn(natural, sides);
    return natural;
  }

  // Install a function that remaps the natural floor index. Pass null
  // to clear. ReverseGateFx uses (i, sides) => (i + sides/2) % sides;
  // identity-clearing on bossEnd is its responsibility.
  setFloorOffset(fn: ((idx: number, sides: number) => number) | null): void {
    this.floorOffsetFn = fn;
  }

  // Flip the gravity sign on Y. ReverseGateFx flips on bossStart,
  // unflips on bossCleared. Idempotent; reset() also restores.
  setGravityFlipped(flipped: boolean): void {
    this.gravitySignY = flipped ? 1 : -1;
    this.applyGravity();
  }

  update(dt: number): void {
    if (this.tweenT < 1 && this.tweenDuration > 0) {
      this.tweenT = Math.min(1, this.tweenT + dt / this.tweenDuration);
      const k = math.easeOutCubic(this.tweenT);
      this.currentAngle =
        this.startAngle + (this.targetAngle - this.startAngle) * k;
      this.worldGroup.rotation.z = this.currentAngle;
    }
    this.applyGravity();
  }

  private applyGravity(): void {
    // The player is fixed at world origin; whichever face is currently
    // rotated to "under the player" sits at world (0, -PLAYER_RADIUS, 0).
    // So gravity is always world-down, regardless of worldGroup rotation.
    // Reverse Gate flips gravitySignY to +1 -- gravity becomes world-up
    // and the player + camera follow (handled by ReverseGateFx +
    // Player.setGravityFlipped).
    this.physics.setGravity(0, this.gravitySignY * GRAVITY_MAGNITUDE, 0);
  }
}
